import { Component, ChangeDetectorRef, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { DragulaService } from 'ng2-dragula';
import { Subscription } from 'rxjs';

import { BaseSmartComponent } from '@/components/base.component';
import { MessageService } from '@/services/message.service';
import { EnvironmentService } from '@/services/EnvironmentService';
import { FilesService } from '@/services/files.service';
import { AuthService } from '@/services/auth.service';
import * as tus from 'tus-js-client';

@Component({
	selector: 'document-upload',
	templateUrl: './documentUpload.template.html',
	host: {'class': 'document-upload-component'},
	animations: [
		trigger('fadeInOut', [
			 state('void', style({
					 opacity: 0,
					 height: 0
			 })),
			 transition('void <=> *', animate(500)),
		 ]),
   ]
})
export class DocumentUploadComponent extends BaseSmartComponent {
	@Input() model: any;
	@Input() label: string = '';
	@Input() type: string = 'unknown'; // job or po
	@Input() typeId: string = 'unknown'; // uuid
	@Input() duplicateRecord: boolean = false;
	@Input() fileSizeError: boolean = false;
	@Input() fileUploadError: boolean = false; // for any errors returned from the ng2-file-upload module
	@Input() isDraggable: boolean = true;
	@Input() disabled: boolean = false;
	@Input() limit: number = 0;

	@Output() queued: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() uploaded: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() duplicateFound: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() saveModel: EventEmitter<any> = new EventEmitter<any>();
	@Output() refreshDocs: EventEmitter<boolean> = new EventEmitter<boolean>();

	public readonly Url: string;
  public duplicateFiles: File[] = [];
  public queuedFiles: File[] = [];
	public hasBaseDropZoneOver: boolean = false;
	public hasAnotherDropZoneOver: boolean = false;
	public response: string = ''; // response from uploader not currently used
	public documentsSorted: string = 'documentsSorted';
  public queuedUploads: any[] = [];
	public maxFileMBs: number = 100; // for displaying in the error message
	private maxFileSize: number = 0;
  private docSubscription: Subscription = new Subscription();

	public get canUpload(): boolean {
	  return !this.disabled && (this.limit === 0 || (this.model.documents && this.model.documents.length < this.limit));
  }


	constructor(
    messageService: MessageService,
    environment: EnvironmentService,
    private changeDetection: ChangeDetectorRef,
    private dragulaService: DragulaService,
    private filesService: FilesService,
    private authService: AuthService
	) {
		super(messageService);
		// todo move this to service
		this.Url = environment.APIBASEURL + '/uploads?ngsw-bypass';

		// todo is this functional? does it do anything?
		this.docSubscription
			.add(dragulaService.dropModel(this.documentsSorted)
			.subscribe(({el, target, source, sourceModel, targetModel, item}) => {
				setTimeout(() => {
					this.save(targetModel);
				},0);
			})
		);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['model']) {
			this.getDocuments();
		}
	}

	override ngOnDestroy(): void {
		this.docSubscription.unsubscribe();
	}

	public onFileSelected(files: File[]): void {
    this.fileSizeError = false;
    for (let index = 0; index < files.length; index++) {
      const file = files[index];

      if (file.size > this.maxFileMBs * 1024 * 1024) {
        this.fileSizeError = true;
        continue;
      }

      const upload = new tus.Upload(file, {
        endpoint: this.Url,
        headers: {
          Authorization: 'Bearer ' + this.authService.accessToken.getValue()
        },
        retryDelays: [0, 3000, 5000, 10_000, 20_000],
        chunkSize: 1024 * 1024 * 3,
        metadata: {
          filename: this.type + '/' + this.typeId + '/' + file.name,
          filetype: file.type,
          contentType: file.type
        },
        onError: (err) => {
          this.onfileUploadError(file, err.message);
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
          this.queuedUploads.find(u => u.name === file.name).progress = percentage;
        },
        onSuccess: () => {
          this.queuedUploads = this.queuedUploads.filter(u => u.name !== file.name);
          this.getDocuments();
          this.refreshDocs.emit(true);
        },
      });

      upload.start();
      this.queuedUploads.push({ name: file.name, progress: 0 });
    }
	}

  public fileOverBase(e: any): void {
    this.hasBaseDropZoneOver = e;
  }

  // hack prevent deleting acknowledgements until those can be handled better
  public isDeletable(file: any): boolean {
    return file.url.indexOf('/acknowledgements/') === -1;
  }

  public removeDocument(file: any): void {
    if (this.warnOnDelete('file')) {
      const self = this;
      if (self.model) {
        var id = self.model._id || self.typeId;
      }
      self.filesService.deleteFile(self.type, id, file.name).then(() => {
        this.duplicateRecord = false;
        self.getDocuments();
        this.refreshDocs.emit(true);
      });
    }
  }

  public save(order: Object): void {
    this.saveModel.emit(order);
  }

  onAddingFiles(files: File[]): void {
    this.duplicateFiles = [];
    this.queuedFiles = [];
    for (let idx = 0; idx < files.length; idx++) {
      const duplicate = this.model.documents.find(d => d.name === files[idx].name);
      if (duplicate) {
        this.duplicateFiles.push(files[idx]);
      } else {
        this.queuedFiles.push(files[idx]);
      }
    }

    if (this.duplicateFiles.length > 0) {
      this.duplicateRecord = true;
      // this.duplicateFound.emit(true);
    } else {
      this.onFileSelected(files);
    }
  }

  private onfileUploadError(item: File, response: string): any {
    let error = JSON.parse(response);
    console.error('File upload error:');
    console.error(error);
    this.fileUploadError = true;
  }

	private getDocuments(): void {
    const self = this;
    this.duplicateRecord = false;
    const id = self.model?._id || self.typeId;
    self.filesService.getFiles(self.type, id)
      .then((files) => {
        if (self.model) {
          self.model.documents = files;
          this.uploaded.emit(true);
          this.queued.emit(false);
        }
      });
	}
}
